import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { format, parse, isValid } from 'date-fns';
import { ja } from 'date-fns/locale';
import Calendar from '../core/Calendar/Calendar';

interface DatePickerDropdownProps {
  value: string; // YYYY-MM-DD format or empty string
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  name?: string;
  id?: string;
  minDate?: Date;
  maxDate?: Date;
  clearable?: boolean;
}

/**
 * DatePickerDropdown - Custom date picker with SailLab-styled calendar
 *
 * Features:
 * - Uses the core Calendar component for date selection
 * - Styled dropdown matching other filter inputs
 * - Portal-based positioning for correct layering
 * - Keyboard navigation support
 * - Optional clear button
 */
const DatePickerDropdown: React.FC<DatePickerDropdownProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  error = false,
  className = '',
  name,
  id,
  minDate,
  maxDate,
  clearable = true,
}) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Parse value string to Date object
  const selectedDate = value ? parse(value, 'yyyy-MM-dd', new Date()) : null;
  const isValidDate = selectedDate && isValid(selectedDate);

  // Format display text based on locale
  const getDisplayText = () => {
    if (!isValidDate || !selectedDate) {
      return placeholder || t('common:datePicker.selectDate', 'Select date');
    }

    if (i18n.language === 'ja') {
      return format(selectedDate, 'yyyy年M月d日', { locale: ja });
    }
    return format(selectedDate, 'MMM d, yyyy');
  };

  const displayText = getDisplayText();
  const hasValue = isValidDate;

  // Calculate position when opening - smart positioning to avoid overflow
  const handleToggle = () => {
    if (!isOpen && triggerRef.current && !disabled) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Calendar dropdown is approximately 320px tall
      const estimatedDropdownHeight = 340;
      const dropdownWidth = 280;

      // Check if there's enough space below
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Position above if not enough space below and more space above
      const shouldPositionAbove = spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow;

      // Check horizontal positioning
      let left = rect.left;
      if (left + dropdownWidth > viewportWidth) {
        left = viewportWidth - dropdownWidth - 8;
      }

      setDropdownStyle({
        position: 'fixed',
        ...(shouldPositionAbove
          ? { bottom: viewportHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }
        ),
        left,
        width: dropdownWidth,
        maxHeight: shouldPositionAbove ? spaceAbove - 8 : spaceBelow - 8,
        zIndex: 1000
      });
    }
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (containerRef.current && !containerRef.current.contains(target) &&
          dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen]);

  const handleDateSelect = (date: Date) => {
    // Format date to YYYY-MM-DD string
    const formattedDate = format(date, 'yyyy-MM-dd');
    onChange(formattedDate);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };

  // Allow all dates to be selectable for filters (past dates are valid for filtering)
  const isDateSelectable = () => true;

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Trigger Button - styled to match filter inputs */}
      <button
        ref={triggerRef}
        type="button"
        id={id}
        name={name}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`filter-trigger ${error ? 'border-red-300' : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={placeholder}
      >
        <span
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.75rem',
            lineHeight: 1,
            color: hasValue ? '#374151' : '#6B7280',
          }}
        >
          {displayText}
        </span>

        <span className="filter-trigger-icon">
          {/* Clear icon - only show when has value */}
          {clearable && hasValue && !disabled ? (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClear(e as any); }}
              className="text-gray-400 hover:text-gray-600 cursor-pointer p-0.5"
              aria-label={t('common:actions.clear', 'Clear')}
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </span>
          ) : (
            <ChevronDownIcon
              className={`h-4 w-4 transition-transform duration-200 ${
                isOpen ? 'transform rotate-180' : ''
              } ${
                error ? 'text-red-400' : disabled ? 'text-gray-300' : 'text-gray-400'
              }`}
            />
          )}
        </span>
      </button>

      {/* Dropdown Menu with Portal */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="bg-white rounded-lg shadow-lg border border-gray-200 p-3"
          style={dropdownStyle}
          role="dialog"
          aria-label={placeholder || t('common:datePicker.selectDate', 'Select date')}
        >
          <Calendar
            selectedDate={isValidDate ? selectedDate : null}
            onDateSelect={handleDateSelect}
            minDate={minDate}
            maxDate={maxDate}
            isDateSelectable={isDateSelectable}
            showNavigation={true}
            compact={true}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default DatePickerDropdown;
